// Campaigns — first real surface. Lists campaigns from the Studio API, with
// status filter pills and click-through to detail.

import { useEffect, useMemo, useState } from 'react';
import { Link, Routes, Route } from 'react-router-dom';
import { api, Campaign } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';
import CampaignDetail from './CampaignDetail';
import CreateCampaign from './CreateCampaign';

const STATUSES: Array<{ id: string; label: string }> = [
  { id: '',          label: 'All' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'active',    label: 'Active' },
  { id: 'draft',     label: 'Drafting' },
  { id: 'paused',    label: 'Paused' },
  { id: 'completed', label: 'Completed' },
];

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(status: string): string {
  switch (status) {
    case 'active':    return T.live;
    case 'draft':     return T.warning;
    case 'paused':    return T.inkMute;
    case 'completed': return T.inkSoft;
    case 'cancelled': return T.error;
    default:          return T.inkSoft;
  }
}

function CampaignsList() {
  const [filter, setFilter] = useState<string>('');
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.campaigns
      .list(filter ? { status: filter } : undefined)
      .then((res) => {
        if (cancelled) return;
        setCampaigns(res.campaigns);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const counts = useMemo(() => {
    if (!campaigns) return null;
    return campaigns.reduce<Record<string, number>>((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    }, {});
  }, [campaigns]);

  return (
    <>
      <PageHeader
        title="Campaigns"
        subtitle="Brief in chat. Vendor routing. Ingest from every platform. One truth back."
        right={
          <Link to="/operator/campaigns/new" style={listStyles.newButton}>
            + New campaign
          </Link>
        }
      />

      {/* Status filter pills */}
      <div style={listStyles.filters}>
        {STATUSES.map((s) => {
          const active = filter === s.id;
          const count = counts && s.id ? counts[s.id] || 0 : null;
          return (
            <button
              key={s.id || 'all'}
              type="button"
              onClick={() => setFilter(s.id)}
              style={{
                ...listStyles.pill,
                ...(active ? listStyles.pillActive : {}),
              }}
            >
              {s.label}
              {count !== null && <span style={listStyles.pillCount}>{count}</span>}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={listStyles.empty}>
          <span style={{ color: T.inkMute, fontStyle: 'normal' }}>Loading campaigns…</span>
        </div>
      )}

      {error && !loading && (
        <div style={listStyles.error}>
          <div style={{ color: T.error, fontFamily: T.font.mono, fontStyle: 'normal', marginBottom: 8 }}>
            Failed to load campaigns
          </div>
          <div style={{ color: T.inkSoft, fontSize: 13, fontFamily: T.font.mono }}>
            {error.message}
          </div>
        </div>
      )}

      {!loading && !error && campaigns && campaigns.length === 0 && (
        <div style={listStyles.empty}>
          <span style={{ color: T.inkMute, fontStyle: 'normal' }}>
            No campaigns {filter ? `with status "${filter}"` : 'yet'}.
          </span>
        </div>
      )}

      {!loading && !error && campaigns && campaigns.length > 0 && (
        <div style={listStyles.grid}>
          {campaigns.map((c) => (
            <Link key={c.id} to={`/operator/campaigns/${c.id}`} style={listStyles.cardLink}>
              <article style={listStyles.card}>
                <div style={listStyles.cardHeader}>
                  <div style={listStyles.cardBrand}>{c.brand_name || '—'}</div>
                  <div style={{ ...listStyles.cardStatus, color: statusColor(c.status) }}>
                    ● {c.status}
                  </div>
                </div>
                <h3 style={listStyles.cardTitle}>{c.title}</h3>
                {c.artist && <div style={listStyles.cardArtist}>{c.artist}{c.track ? ` · ${c.track}` : ''}</div>}

                <div style={listStyles.cardBudgetRow}>
                  <div>
                    <div style={listStyles.budgetLabel}>budget</div>
                    <div style={listStyles.budgetValue}>
                      {formatMoney(c.budget_spent)} <span style={listStyles.budgetSlash}>/</span> {formatMoney(c.budget_total)}
                    </div>
                  </div>
                  <div>
                    <div style={listStyles.budgetLabel}>window</div>
                    <div style={listStyles.budgetValue}>
                      {formatDate(c.starts_at)}<span style={listStyles.budgetSlash}> → </span>{formatDate(c.ends_at)}
                    </div>
                  </div>
                </div>

                {/* progress bar */}
                <div style={listStyles.progressTrack}>
                  <div
                    style={{
                      ...listStyles.progressFill,
                      width: `${Math.min(100, (c.budget_total ? (c.budget_spent / c.budget_total) * 100 : 0))}%`,
                    }}
                  />
                </div>
              </article>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export default function Campaigns() {
  return (
    <Routes>
      <Route index element={<CampaignsList />} />
      <Route path="new" element={<CreateCampaign />} />
      <Route path=":id" element={<CampaignDetail />} />
    </Routes>
  );
}

const listStyles: Record<string, React.CSSProperties> = {
  filters: {
    display: 'flex',
    gap: 8,
    marginBottom: 32,
    flexWrap: 'wrap' as const,
  },
  pill: {
    background: 'transparent',
    border: `1px solid ${T.rule}`,
    color: T.inkSoft,
    fontFamily: T.font.body,
    fontSize: 12,
    letterSpacing: '0.06em',
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, color 0.2s, background 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  pillActive: {
    borderColor: T.accent,
    color: T.ink,
    background: T.accentSoft,
  },
  pillCount: {
    fontFamily: T.font.mono,
    fontSize: 10,
    color: T.accent,
    opacity: 0.85,
  },
  newButton: {
    fontFamily: T.font.body,
    fontSize: 12,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.accent,
    border: `1px solid ${T.accent}`,
    padding: '10px 18px',
    textDecoration: 'none',
    transition: 'background 0.2s, color 0.2s',
    background: 'transparent',
  },
  empty: {
    padding: 64,
    textAlign: 'center' as const,
    border: `1px solid ${T.rule}`,
    fontSize: 14,
  },
  error: {
    padding: 32,
    border: `1px solid ${T.error}`,
    background: T.errorSoft,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))',
    gap: 18,
  },
  cardLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  card: {
    padding: 26,
    border: `1px solid ${T.rule}`,
    background: 'transparent',
    transition: 'border-color 0.3s, transform 0.3s',
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 14,
  },
  cardBrand: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 13,
    color: T.inkSoft,
    letterSpacing: '0.04em',
  },
  cardStatus: {
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    fontFamily: T.font.mono,
    fontStyle: 'normal',
  },
  cardTitle: {
    fontFamily: T.font.display,
    fontSize: 22,
    color: T.ink,
    letterSpacing: '0.02em',
    lineHeight: 1.2,
    marginBottom: 6,
    fontWeight: 400,
  },
  cardArtist: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 14,
    color: T.accent,
    marginBottom: 18,
  },
  cardBudgetRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
    marginTop: 'auto',
    paddingTop: 14,
    borderTop: `1px solid ${T.rule}`,
  },
  budgetLabel: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 10,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
    color: T.inkMute,
    marginBottom: 4,
  },
  budgetValue: {
    fontFamily: T.font.mono,
    fontSize: 13,
    color: T.ink,
  },
  budgetSlash: {
    color: T.inkMute,
    margin: '0 4px',
  },
  progressTrack: {
    marginTop: 14,
    height: 2,
    background: T.rule,
  },
  progressFill: {
    height: '100%',
    background: T.accent,
    transition: 'width 0.4s',
  },
};
